import * as XLSX from 'xlsx';
import {
    getAsRepairExcelColumnMapping,
    getAsRepairExcelFieldMap,
} from './asRepairExcelImport';
import {
    AS_REPAIR_MANAGEMENT_SCOPE,
    formatAsRepairAutoDisplay,
    formatAsRepairQuantityDisplay,
    getAsRepairFieldDisplayValue,
    normalizeAsRepairFormConfig,
    parseQuantityValue,
} from './asRepairFormConfig';

/** A/S 개인 관리 — 수리완료 리스트 다운로드 기본 열 (A열부터 연속) */
const PERSONAL_REPAIR_COMPLETE_FIXED_COLUMNS = [
    { role: 'boxNumber', header: '박스번호' },
    { role: 'modelName', header: '모델명' },
    { role: 'itemName', header: '품목명' },
    { role: 'totalQty', header: '전체수량' },
    { role: 'siteName', header: '현장명' },
    { role: 'memo', header: '비고사항' },
];

const shouldAlwaysShowAsRepairDetailField = (field) => {
    const label = String(field?.label || '').replace(/\s+/g, '');
    return label.includes('실입고수량') || label.includes('진행중수량');
};

/** 리스트 세부사항과 동일한 필드 순서 */
export const getAsRepairDetailExportFields = (config, listSummary, excludeKeys = new Set()) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const summaryKeys = listSummary?.summaryKeys || new Set();
    return fields.filter((field) => {
        if (!field?.key || excludeKeys.has(field.key)) {
            return false;
        }
        return (
            !summaryKeys.has(field.key)
            || field.inputType === 'textarea'
            || field.inputType === 'text'
            || field.inputType === 'quantity'
            || shouldAlwaysShowAsRepairDetailField(field)
        );
    });
};

const cellValueForExport = (field, row, config, role = null) => {
    if (!field) {
        return '';
    }
    const raw = getAsRepairFieldDisplayValue(field, row, config);
    if (raw == null || raw === '') {
        return '';
    }
    if (field.inputType === 'auto') {
        const text = formatAsRepairAutoDisplay(raw);
        return text === '' ? '' : text;
    }
    if (field.inputType === 'quantity' || role === 'totalQty') {
        const text = formatAsRepairQuantityDisplay(raw);
        if (text !== '') {
            return parseQuantityValue(raw);
        }
        return '';
    }
    return String(raw);
};

/** A/S 개인 관리 — 수리완료 리스트 (A~H 고정 + J부터 세부사항 순서) */
export const buildAsRepairPersonalRepairCompleteExcelRows = (rows, config, listSummary) => {
    const list = Array.isArray(rows) ? rows : [];
    const formConfig = normalizeAsRepairFormConfig(config);
    const fieldMap = getAsRepairExcelFieldMap(formConfig, AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL);
    const fixedFieldKeys = new Set(
        PERSONAL_REPAIR_COMPLETE_FIXED_COLUMNS
            .map((col) => fieldMap[col.role]?.key)
            .filter(Boolean),
    );
    const detailFields = getAsRepairDetailExportFields(formConfig, listSummary, fixedFieldKeys);
    const headers = [
        ...PERSONAL_REPAIR_COMPLETE_FIXED_COLUMNS.map((col) => col.header),
        ...detailFields.map((field) => field.label || field.key),
    ];

    const bodyRows = list.map((row) => {
        const fixedCells = PERSONAL_REPAIR_COMPLETE_FIXED_COLUMNS.map((col) => (
            cellValueForExport(fieldMap[col.role], row, formConfig, col.role)
        ));
        const detailCells = detailFields.map((field) => (
            cellValueForExport(field, row, formConfig)
        ));
        return [...fixedCells, ...detailCells];
    });

    return [headers, ...bodyRows];
};

/** A/S 행 → 업로드와 동일한 열 배열 (헤더 없음, 개인 관리는 A/B/C/F/G/H 위치 유지) */
export const buildAsRepairExcelRows = (rows, config, { managementScope } = {}) => {
    const list = Array.isArray(rows) ? rows : [];
    const scope = managementScope || AS_REPAIR_MANAGEMENT_SCOPE.REPAIR;
    const formConfig = normalizeAsRepairFormConfig(config);
    const columnMapping = getAsRepairExcelColumnMapping(scope);
    const fieldMap = getAsRepairExcelFieldMap(formConfig, scope);
    const maxIndex = columnMapping.reduce((max, col) => Math.max(max, col.index), 0);

    return list.map((row) => {
        const line = Array(maxIndex + 1).fill('');
        columnMapping.forEach(({ index, role }) => {
            line[index] = cellValueForExport(fieldMap[role], row, formConfig, role);
        });
        return line;
    });
};

const buildExportFileName = (managementScope, { repairComplete = false } = {}) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    let prefix = 'AS수리목록';
    if (managementScope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL) {
        prefix = repairComplete ? 'AS수리완료목록' : 'AS개인목록';
    }
    return `${prefix}_${stamp}.xlsx`;
};

/** 선택한 A/S 목록을 XLSX로 저장 */
export const downloadAsRepairExcelFile = (
    rows,
    config,
    { fileName, managementScope, listSummary, repairComplete = false } = {},
) => {
    const scope = managementScope || AS_REPAIR_MANAGEMENT_SCOPE.REPAIR;
    const isPersonalRepairComplete = scope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL && repairComplete;
    const dataRows = isPersonalRepairComplete
        ? buildAsRepairPersonalRepairCompleteExcelRows(rows, config, listSummary)
        : buildAsRepairExcelRows(rows, config, { managementScope: scope });
    if (dataRows.length === 0) {
        return { ok: false, reason: 'empty' };
    }

    const sheet = XLSX.utils.aoa_to_sheet(dataRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1');
    XLSX.writeFile(
        workbook,
        fileName || buildExportFileName(scope, { repairComplete: isPersonalRepairComplete }),
    );
    return { ok: true, rowCount: dataRows.length };
};
